
/*
 * Aasraa Configurator V1.1
 * Compatible variant selection with native Shopify integration.
 * No changes to global.js, Liquid or cart drawer required.
 */
(() => {
  "use strict";

  const formatPrice = cents =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0
    }).format(cents / 100);

  const escapeHTML = value =>
    String(value).replace(/[&<>"']/g, character => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    })[character]);

  const wait = ms =>
    new Promise(resolve => setTimeout(resolve, ms));

  const same = (a, b) =>
    JSON.stringify(a) === JSON.stringify(b);

  function getPicker(root) {
    return document.getElementById(
      "variant-radios-" + root.dataset.section
    );
  }

  function getFieldsets(picker) {
    return Array.from(
      picker?.querySelectorAll("fieldset") || []
    ).filter(fieldset =>
      fieldset.querySelector('input[type="radio"]')
    );
  }

  function readSelections(picker) {
    return getFieldsets(picker).map(fieldset =>
      fieldset.querySelector('input[type="radio"]:checked')
        ?.value || ""
    );
  }

  function getVariants(picker) {
    try {
      return JSON.parse(
        picker.querySelector(
          'script[type="application/json"]'
        ).textContent
      );
    } catch {
      return [];
    }
  }

  function init(root) {
    if (root.dataset.aacInitialized) return;

    const picker = getPicker(root);
    if (!picker) return;

    const variants = getVariants(picker);

    if (!Array.isArray(variants) || !variants.length) {
      return;
    }

    const availableVariants = variants.filter(
      variant => variant.available
    );

    const overlay = root.querySelector(
      "[data-aac-overlay]"
    );

    const optionsHost = root.querySelector(
      "[data-aac-options]"
    );
    const error = root.querySelector(
      "[data-aac-error]"
    );
    const applyButton = root.querySelector(
      "[data-aac-apply]"
    );
    const summaryElement = root.querySelector(
      "[data-aac-summary]"
    );
    const totalElement = root.querySelector(
      "[data-aac-total]"
    );

    if (
      !overlay ||
      !optionsHost ||
      !error ||
      !applyButton ||
      !summaryElement ||
      !totalElement
    ) return;

    let staged = [];
    let returnFocus = null;
    let busy = false;
    let adjustmentMessage = "";

    function findVariant(values) {
      return variants.find(
        variant => same(variant.options, values)
      );
    }

    function findAvailable(values) {
      return availableVariants.find(
        variant => same(variant.options, values)
      );
    }

    /*
     * Find the closest available variant after a choice.
     *
     * The newly selected option is mandatory.
     * Other matching selections are preserved wherever possible.
     */
    function closestAvailable(values, changedIndex) {
      const candidates = availableVariants.filter(
        variant =>
          variant.options[changedIndex] ===
          values[changedIndex]
      );

      if (!candidates.length) return null;

      const ranked = candidates.map(variant => {
        let score = 0;

        variant.options.forEach((option, index) => {
          if (
            index !== changedIndex &&
            option === values[index]
          ) {
            score += 1;
          }
        });

        return { variant, score };
      });

      ranked.sort((a, b) => b.score - a.score);

      return ranked[0].variant;
    }

    function optionGroups() {
      return getFieldsets(getPicker(root)).map(
        (fieldset, index) => {
          const radios = Array.from(
            fieldset.querySelectorAll(
              'input[type="radio"]'
            )
          );

          const swatches = Array.from(
            fieldset.querySelectorAll(".swatch-element")
          );

          return {
            name:
              fieldset.querySelector("legend")
                ?.textContent.trim() ||
              `Option ${index + 1}`,

            choices: radios.map(radio => {
              const swatch = swatches.find(
                element =>
                  element.dataset.value === radio.value
              );

              const image = swatch?.querySelector(
                ".swatch-variant-image"
              );

              const colour = swatch?.querySelector(
                ".swatch-variant-color"
              );

              return {
                value: radio.value,
                image:
                  image?.style.backgroundImage || "",
                colour:
                  colour?.style.backgroundColor || ""
              };
            })
          };
        }
      );
    }

    function updateSummary() {
      const values = readSelections(getPicker(root));

      summaryElement.textContent =
        values.filter(Boolean).join(" · ") ||
        "Choose your available options";
    }

    function render() {
      const groups = optionGroups();
      const selectedVariant = findVariant(staged);
      const validVariant = findAvailable(staged);

      optionsHost.innerHTML = groups.map(
        (group, index) => {
          const isColour = /colou?r/i.test(group.name);

          const choices = group.choices.map(choice => {
            const candidates = availableVariants.filter(
              variant =>
                variant.options[index] === choice.value
            );

            const enabled = candidates.length > 0;

            const previewValues = [...staged];
            previewValues[index] = choice.value;

            const previewVariant =
              closestAvailable(previewValues, index);

            let visual = "";

            if (
              isColour &&
              choice.image &&
              choice.image !== "none"
            ) {
              visual = `
                <span
                  class="aac-colour-photo"
                  style="background-image:${escapeHTML(
                    choice.image
                  )}"
                ></span>
              `;
            } else if (
              isColour &&
              choice.colour
            ) {
              visual = `
                <span
                  class="aac-colour-swatch"
                  style="background-color:${escapeHTML(
                    choice.colour
                  )}"
                ></span>
              `;
            }

            const price =
              !isColour && previewVariant
                ? `<span class="aac-choice-price">
                    ${formatPrice(previewVariant.price)}
                   </span>`
                : "";

            return `
              <button
                type="button"
                class="aac-choice"
                data-aac-option="${index}"
                data-aac-value="${escapeHTML(choice.value)}"
                aria-pressed="${
                  staged[index] === choice.value
                }"
                ${enabled ? "" : "disabled"}
              >
                ${visual}
                <span class="aac-choice-name">
                  ${escapeHTML(choice.value)}
                </span>
                ${price}
                ${
                  enabled
                    ? ""
                    : `<span class="aac-choice-note">
                         Unavailable
                       </span>`
                }
              </button>
            `;
          }).join("");

          return `
            <section class="aac-group">
              <div class="aac-group-head">
                <h3>
                  ${index + 1}. ${escapeHTML(group.name)}
                </h3>
                <span class="aac-group-selected">
                  ${escapeHTML(staged[index] || "")}
                </span>
              </div>

              <div class="aac-choices ${
                isColour ? "aac-colours" : ""
              }">
                ${choices}
              </div>
            </section>
          `;
        }
      ).join("");

      totalElement.textContent = selectedVariant
        ? formatPrice(selectedVariant.price)
        : "Choose options";

      applyButton.disabled = !validVariant || busy;

      if (adjustmentMessage) {
        const note = document.createElement("p");
        note.className = "aac-choice-note";
        note.setAttribute("role", "status");
        note.style.marginTop = "12px";
        note.textContent = adjustmentMessage;
        optionsHost.appendChild(note);
      }

      error.hidden = true;
    }

    
function open() {
  staged = readSelections(getPicker(root));
  adjustmentMessage = "";
  returnFocus = document.activeElement;

  render();

  // All event handlers are already initialised.
  // Move only the popup overlay above the theme layout.
  if (overlay.parentElement !== document.body) {
    document.body.appendChild(overlay);
  }

  overlay.hidden = false;
  document.body.classList.add("aac-modal-open");

  overlay.querySelector("[data-aac-close]").focus();
}


    function close() {
      if (busy) return;

      overlay.hidden = true;
      document.body.classList.remove("aac-modal-open");

      returnFocus?.focus?.();
    }

    root.querySelector(
      "[data-aac-open]"
    ).addEventListener("click", open);

    root.querySelector(
      "[data-aac-close]"
    ).addEventListener("click", close);

    overlay.addEventListener("click", event => {
      if (event.target === overlay) close();
    });

    overlay.addEventListener("keydown", event => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== "Tab") return;

      const focusable = Array.from(
        overlay.querySelectorAll(
          'button:not([disabled])'
        )
      );

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) return;

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    });

    optionsHost.addEventListener("click", event => {
      const button = event.target.closest(
        "[data-aac-option]"
      );

      if (!button || button.disabled) return;

      const index = Number(
        button.dataset.aacOption
      );

      const nextValues = [...staged];
      nextValues[index] = button.dataset.aacValue;

      const compatible = closestAvailable(
        nextValues,
        index
      );

      if (!compatible) return;

      const previous = [...staged];

      staged = [...compatible.options];

      const changedOthers = staged.some(
        (value, optionIndex) =>
          optionIndex !== index &&
          value !== previous[optionIndex]
      );

      adjustmentMessage = changedOthers
        ? "Other selections were adjusted to match an available configuration. Please review your choices before applying."
        : "";

      render();

      const selectedButton = Array.from(
        optionsHost.querySelectorAll(
          "[data-aac-option]"
        )
      ).find(element =>
        Number(element.dataset.aacOption) === index &&
        element.dataset.aacValue === staged[index]
      );

      selectedButton?.focus();
    });

    applyButton.addEventListener(
      "click",
      async () => {
        if (busy) return;

        const target = findAvailable(staged);

        if (!target) {
          error.textContent =
            "This configuration is unavailable.";
          error.hidden = false;
          return;
        }

        busy = true;
        applyButton.disabled = true;
        applyButton.textContent = "Applying…";

        try {
          /*
           * Apply through the existing native picker.
           * This preserves Shopify's theme event handling.
           */
          for (let index = 0; index < staged.length; index++) {
            const livePicker = getPicker(root);
            const fieldset =
              getFieldsets(livePicker)[index];

            const radio = Array.from(
              fieldset?.querySelectorAll(
                'input[type="radio"]'
              ) || []
            ).find(
              element => element.value === staged[index]
            );

            if (!radio) {
              throw new Error(
                "An option changed. Please reopen the configurator."
              );
            }

            if (!radio.checked) {
              radio.checked = true;

              radio.dispatchEvent(
                new Event("change", {
                  bubbles: true
                })
              );

              await wait(250);
            }
          }

          await wait(450);

          const current = readSelections(
            getPicker(root)
          );

          if (!same(current, staged)) {
            throw new Error(
              "Could not apply this configuration. Please try again."
            );
          }

          updateSummary();

          busy = false;
          close();
        } catch (exception) {
          error.textContent =
            exception.message ||
            "Unable to apply configuration.";

          error.hidden = false;
        } finally {
          busy = false;

          applyButton.disabled =
            !findAvailable(staged);

          applyButton.innerHTML =
            'Apply configuration <span aria-hidden="true">→</span>';
        }
      }
    );

    picker.addEventListener("change", () => {
      if (overlay.hidden) updateSummary();
    });

    root.dataset.aacInitialized = "1";
    picker.classList.add("aac-native-picker");
    document.body.classList.add("aac-ready");

    updateSummary();
  }

  function boot() {
    document.querySelectorAll(
      "[data-aac-root]"
    ).forEach(init);
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      boot
    );
  } else {
    boot();
  }

  document.addEventListener(
    "shopify:section:load",
    boot
  );
})();
